import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useOutletContext, useParams, useLocation } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { OrgHub } from '@/components/hub/OrgHub';
import { getDashboardRedirectForRole } from '@/lib/permissions';

import { TeamDashboard } from '@/components/dashboard/TeamDashboard';
import { AgentDashboard } from '@/components/dashboard/AgentDashboard';
import { ManagerDashboard } from '@/components/dashboard/ManagerDashboard';
import { CreateTicket } from '@/components/console/CreateTicket';
import { CommandPalette } from '@/components/CommandPalette';
import { ReportsPage } from '@/components/reports/ReportsPage';
import { WorkQueuePage } from '@/components/queue/WorkQueuePage';
import { AuditPage } from '@/components/audit/AuditPage';
import { SettingsPage } from '@/components/SettingsPage';
import { TeamManagementPage } from '@/components/TeamManagementPage';
import { UserManagementPage } from '@/components/UserManagementPage';
import { TicketConfigurationPage } from '@/components/TicketConfigurationPage';
import { AutomationPage } from '@/components/AutomationPage';
import { DataMigrationPage } from '@/components/DataMigrationPage';
import { IntegrationsPage } from '@/components/IntegrationsPage';
import { SSOConfigurationPage } from '@/components/SSOConfigurationPage';
import { EmailConfigurationPage } from '@/components/EmailConfigurationPage';
import { SocUseCaseHub } from '@/components/soc/SocUseCaseHub';
import { SentinelIncidentList } from '@/components/soc/SentinelIncidentList';
import { MicrosoftSentinelSettings } from '@/components/soc/MicrosoftSentinelSettings';
import { NormalUserPortal } from '@/components/portal/NormalUserPortal';
import { UserDashboardLayout } from '@/components/soc/user/UserDashboardLayout';
import { UserDashboardHome } from '@/components/soc/user/UserDashboardHome';
import { UserUseCasesList } from '@/components/soc/user/UserUseCasesList';
import { UserCreateUseCase } from '@/components/soc/user/UserCreateUseCase';
import { UserDraftsList } from '@/components/soc/user/UserDraftsList';
import { UserCompletedUseCases } from '@/components/soc/user/UserCompletedUseCases';
import { UserPendingActions } from '@/components/soc/user/UserPendingActions';
import { UserInProgressView } from '@/components/soc/user/UserInProgressView';
import { UserWorkflowHistory } from '@/components/soc/user/UserWorkflowHistory';
import { UserNotificationsView } from '@/components/soc/user/UserNotificationsView';
import { UserEmailHistoryView } from '@/components/soc/user/UserEmailHistoryView';
import { UserKnowledgeBase } from '@/components/soc/user/UserKnowledgeBase';
import { UserGuidelinesView } from '@/components/soc/user/UserGuidelinesView';
import { UserProfileView } from '@/components/soc/user/UserProfileView';
import { UserChangePasswordView } from '@/components/soc/user/UserChangePasswordView';
import { AuthScreen } from '@/components/auth/AuthScreen';
import { AIChatWidget } from '@/components/AIChatWidget';
import { ForcePasswordChangeScreen } from '@/components/auth/ForcePasswordChangeScreen';
import { AccessDenied } from '@/components/AccessDenied';
import { TicketDirectView } from '@/components/TicketDirectView';

import type { Ticket } from '@/lib/mock';
import { useConsole } from '@/lib/data';
import { useEscalationEngine } from '@/lib/escalation';

import { useAuth } from '@/lib/auth';
import { useNow } from '@/lib/useNow';
import { GlobalTicketSlideover } from '@/components/GlobalTicketSlideover';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Outlet } from 'react-router-dom';
import { TEAMS } from '@/lib/teams';

function ConsoleLayout() {
  const { user } = useAuth();
  const meName = user?.displayName || user?.email || 'A. Khan';
  const now = useNow(1000);
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  const { 
    tickets, source, isLoading, error, createTicket, setStatus, assign, addUpdate, 
    updateField, relate, merge, bulkAssign, bulkStatus, bulkUpdate, addAttachment, removeAttachment, 
    resolveTicket, setParent, escalate, requestApproval, decideApproval, refreshTickets 
  } = useConsole(meName);
  useEscalationEngine(tickets, escalate);
  
  const [quickOpen, setQuickOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [focusTicketId, setFocusTicketId] = useState<string | null>(null);

  const navigate = useNavigate();

  // ⌘K palette + global `c` to create (disabled for SOC users).
  useEffect(() => {
    if (isSocUser) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      const el = e.target as HTMLElement;
      const typing = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
      if (!typing && !e.metaKey && !e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        setQuickOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSocUser]);

  const openTicket = (t: Ticket) => {
    setFocusTicketId(t.id);
    setPaletteOpen(false);
  };

  const handleHeaderNavigate = (v: string) => {
    if (isSocUser || v === 'soc') {
      navigate('/admin/soc');
      return;
    }
    if (v === 'hub') {
      if (role === 'manager') navigate('/manager/dashboard');
      else if (role === 'technician' || role.includes('technician') || role === 'agent' || role.includes('service desk')) navigate('/technician/dashboard');
      else if (role === 'viewer' || role === 'auditor') navigate('/viewer/dashboard');
      else navigate('/admin/dashboard');
    }
    else if (v === 'queue') {
      if (role === 'technician' || role.includes('technician') || role === 'agent' || role.includes('service desk')) navigate('/technician/queue');
      else if (role === 'manager') navigate('/manager/queue');
      else navigate('/admin/queue');
    }
    else if (v === 'reports') {
      if (role === 'manager') navigate('/manager/reports');
      else if (role === 'viewer' || role === 'auditor') navigate('/viewer/reports');
      else navigate('/admin/reports');
    }
    else if (v === 'audit') navigate('/admin/audit');
    else if (v === 'settings') navigate('/admin/settings/profile');
    else if (v === 'users') navigate('/admin/users');
    else if (v === 'teams') navigate('/admin/teams');
  };

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-theme text-theme transition-colors">
      <AppHeader 
        source={source} 
        tickets={tickets} 
        now={now} 
        onNavigate={handleHeaderNavigate} 
        onNew={() => setQuickOpen(true)} 
        onSearch={() => setPaletteOpen(true)} 
      />
      <main className="flex min-h-0 flex-1 relative">
        <div className="w-full h-full min-h-0 overflow-hidden">
          <Outlet context={{ tickets, source, now, openTicket, setFocusTicketId, onNew: () => setQuickOpen(true), assign, setStatus, bulkAssign, bulkStatus, bulkUpdate, refreshTickets, isLoading, error }} />
        </div>
      </main>

      {!isSocUser && (
        <CreateTicket
          open={quickOpen}
          initialTeamId={null}
          onClose={() => setQuickOpen(false)}
          onCreate={createTicket}
          onSelectTicket={(id) => setFocusTicketId(id)}
        />
      )}

      {!isSocUser && (
        <GlobalTicketSlideover
          ticketId={focusTicketId}
          tickets={tickets}
          now={now}
          onClose={() => setFocusTicketId(null)}
          actions={{
            onStatus: setStatus,
            onAssign: assign,
            onUpdate: addUpdate,
            onRelate: relate,
            onMerge: merge,
            onBulkAssign: bulkAssign,
            onBulkStatus: bulkStatus,
            onBulkUpdate: bulkUpdate,
            onAddAttachment: addAttachment,
            onRemoveAttachment: removeAttachment,
            onResolve: resolveTicket,
            onSetParent: setParent,
            onUpdateField: updateField,
            onRequestApproval: requestApproval,
            onDecideApproval: decideApproval,
          }}
        />
      )}
      
      <CommandPalette 
        open={paletteOpen} 
        onClose={() => setPaletteOpen(false)}
        tickets={tickets}
        onOpenTicket={openTicket}
        onOpenTeam={(id) => navigate(`/admin/team/${id}`)}
      />
      <AIChatWidget onCreateTicket={createTicket} tickets={tickets} />
    </div>
  );
}

function AdminHubWrapper() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  if (isSocUser) {
    return <Navigate to="/admin/soc" replace />;
  }

  const { tickets, onNew } = useOutletContext<any>();
  const navigate = useNavigate();
  return <OrgHub onOpenTeam={(id) => navigate(`/admin/team/${id}`)} tickets={tickets} onNew={onNew} />;
}

function AdminReportsWrapper() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  if (isSocUser) {
    return <Navigate to="/admin/soc" replace />;
  }

  const { tickets, now } = useOutletContext<any>();
  const navigate = useNavigate();
  return <ReportsPage tickets={tickets} now={now} onBack={() => navigate('/admin/dashboard')} />;
}

function AdminAuditWrapper() {
  const { tickets } = useOutletContext<any>();
  return <AuditPage tickets={tickets} />;
}

function AdminTeamWrapper() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  if (isSocUser) {
    return <Navigate to="/admin/soc" replace />;
  }

  const { id } = useParams<{ id: string }>();
  const { tickets, now, openTicket, onNew } = useOutletContext<any>();
  const navigate = useNavigate();
  const team = TEAMS.find(t => t.slug === id || t.id === id);
  
  if (!team) return <div className="p-6 text-muted-foreground">Team not found</div>;
  
  return (
    <TeamDashboard 
      team={team} 
      tickets={tickets} 
      now={now} 
      onBack={() => navigate('/admin/dashboard')}
      onNew={onNew}
      onOpenTicket={(ticketId) => openTicket(tickets.find((t: any) => t.id === ticketId)!)} 
    />
  );
}

function AdminQueueWrapper() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  if (isSocUser) {
    return <Navigate to="/admin/soc" replace />;
  }

  const { tickets, now, openTicket, bulkAssign, bulkStatus, bulkUpdate } = useOutletContext<any>();
  const userName = user?.displayName || user?.name || 'Barry Allen';
  const navigate = useNavigate();
  
  return (
    <WorkQueuePage 
      tickets={tickets} 
      now={now || Date.now()} 
      me={userName} 
      myUserId={user?.id}
      onOpenTicket={openTicket}
      bulkAssign={bulkAssign}
      bulkStatus={bulkStatus}
      bulkUpdate={bulkUpdate}
      onBack={() => navigate('/admin/dashboard')}
    />
  );
}

function TechnicianQueueWrapper() {
  const { tickets, now, openTicket, bulkAssign, bulkStatus, bulkUpdate } = useOutletContext<any>();
  const { user } = useAuth();
  const userName = user?.displayName || user?.name || 'Barry Allen';
  const navigate = useNavigate();
  
  return (
    <WorkQueuePage 
      tickets={tickets} 
      now={now || Date.now()} 
      me={userName} 
      myUserId={user?.id}
      onOpenTicket={openTicket}
      bulkAssign={bulkAssign}
      bulkStatus={bulkStatus}
      bulkUpdate={bulkUpdate}
      onBack={() => navigate('/technician/dashboard')}
    />
  );
}

function AgentDashboardWrapper() {
  const { tickets, openTicket, refreshTickets, isLoading, error } = useOutletContext<any>();
  return <AgentDashboard tickets={tickets} onOpenTicket={openTicket} onRefresh={refreshTickets} isLoading={isLoading} error={error} />;
}

function ManagerDashboardWrapper() {
  const { tickets } = useOutletContext<any>();
  return <ManagerDashboard tickets={tickets} />;
}

function ViewerDashboardWrapper() {
  const { tickets, now } = useOutletContext<any>();
  return <ReportsPage tickets={tickets} now={now} />;
}

export default function App() {
  const { session, initialLoading } = useAuth();
  const navigate = useNavigate();

  if (initialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme transition-colors">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
      </div>
    );
  }

  const user = session?.user;
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  const handleBack = () => {
    if (window.location.pathname.startsWith('/admin/soc')) {
      navigate('/user/dashboard');
    } else {
      navigate(isSocUser ? '/admin/soc' : '/admin/dashboard');
    }
  };

  return (
    <Routes>
      <Route path="/login" element={!session ? <AuthScreen /> : <Navigate to="/" replace />} />
        <Route path="/403" element={<AccessDenied />} />
      <Route path="/tickets/:ticketRef" element={<TicketDirectView />} />

      {/* Root redirector based on authenticated role */}
      <Route path="/" element={<ProtectedRoute />}>
        <Route index element={<RoleRedirector />} />
      </Route>

      {/* ADMIN & SUPER ADMIN ROUTES */}
      <Route element={<ProtectedRoute allowedRoles={['Super Admin', 'superadmin', 'Admin', 'admin', 'SOC Admin', 'soc admin', 'SOC Lead', 'SOC Manager', 'SOC Analyst', 'Technician', 'technician']} requiredPermission="ticket.view" />}>
        <Route path="/admin" element={<ConsoleLayout />}>
          <Route index element={<RoleRedirector />} />
          <Route path="dashboard" element={<AdminHubWrapper />} />
          <Route path="queue" element={<AdminQueueWrapper />} />
          <Route path="team/:id" element={<AdminTeamWrapper />} />
          <Route path="reports" element={<AdminReportsWrapper />} />
          <Route path="audit" element={<AdminAuditWrapper />} />
          
          <Route path="/admin/settings/profile" element={<SettingsPage onBack={handleBack} initialTab="profile" />} />
          <Route path="/admin/settings/departments" element={<SettingsPage onBack={handleBack} initialTab="departments" />} />
          
          <Route element={<ProtectedRoute requiredPermission="user.view" />}>
            <Route path="/admin/users" element={<UserManagementPage onBack={handleBack} initialTab="users" />} />
            <Route path="/admin/users/roles" element={<UserManagementPage onBack={handleBack} initialTab="roles" />} />
            <Route path="/admin/users/permissions" element={<UserManagementPage onBack={handleBack} initialTab="permissions" />} />
          </Route>
          
          <Route element={<ProtectedRoute requiredPermission="team.view" />}>
            <Route path="/admin/teams" element={<TeamManagementPage onBack={handleBack} />} />
          </Route>
          
          <Route element={<ProtectedRoute requiredPermission="settings.manage" />}>
            <Route path="/admin/tickets/categories" element={<TicketConfigurationPage onBack={handleBack} initialTab="categories" />} />
            <Route path="/admin/tickets/priorities" element={<TicketConfigurationPage onBack={handleBack} initialTab="priorities" />} />
            <Route path="/admin/tickets/workflow" element={<TicketConfigurationPage onBack={handleBack} initialTab="workflow" />} />
            <Route path="/admin/tickets/custom-fields" element={<TicketConfigurationPage onBack={handleBack} initialTab="custom-fields" />} />
          </Route>
          
          <Route element={<ProtectedRoute requiredPermission="automation.view" />}>
            <Route path="/admin/automation/assignment" element={<AutomationPage onBack={handleBack} initialTab="assignment" />} />
            <Route path="/admin/automation/escalation" element={<AutomationPage onBack={handleBack} initialTab="escalation" />} />
            <Route path="/admin/automation/sla" element={<AutomationPage onBack={handleBack} initialTab="sla" />} />
            <Route path="/admin/automation/approvals" element={<AutomationPage onBack={handleBack} initialTab="approvals" />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="integration.view" />}>
            <Route path="/admin/email/smtp" element={<EmailConfigurationPage onBack={handleBack} initialTab="smtp" />} />
            <Route path="/admin/email/mailboxes" element={<EmailConfigurationPage onBack={handleBack} initialTab="mailboxes" />} />
            <Route path="/admin/email/ticket-portal" element={<EmailConfigurationPage onBack={handleBack} initialTab="ticket-portal" />} />
            <Route path="/admin/integrations" element={<IntegrationsPage onBack={handleBack} />} />
            <Route path="/admin/sso-config" element={<SSOConfigurationPage onBack={handleBack} />} />
          </Route>
          
          <Route element={<ProtectedRoute allowedRoles={['Super Admin', 'superadmin']} requiredPermission="organization.delete" />}>
            <Route path="/admin/migration" element={<DataMigrationPage onBack={handleBack} />} />
          </Route>

          {/* AZURE SENTINEL INTEGRATION - STRICTLY SUPERADMIN ONLY */}
          <Route element={<ProtectedRoute allowedRoles={['Super Admin', 'superadmin']} />}>
            <Route path="/admin/integrations/sentinel" element={<MicrosoftSentinelSettings />} />
          </Route>

          {/* SOC USE CASE GOVERNANCE ROUTES */}
          <Route path="/admin/soc" element={<SocUseCaseHub onBack={handleBack} />} />
          <Route path="/admin/soc-use-cases" element={<SocUseCaseHub onBack={handleBack} />} />
          <Route path="/admin/soc/sentinel-incidents" element={<SentinelIncidentList />} />
        </Route>
      </Route>

      {/* MANAGER ROUTES */}
      <Route element={<ProtectedRoute allowedRoles={['Manager', 'Admin', 'admin', 'Super Admin', 'superadmin']} requiredPermission="team.view" />}>
        <Route element={<ConsoleLayout />}>
          <Route path="/manager/dashboard" element={<ManagerDashboardWrapper />} />
          <Route path="/manager/queue" element={<AdminQueueWrapper />} />
          <Route path="/manager/reports" element={<AdminReportsWrapper />} />
        </Route>
      </Route>

      {/* AGENT & TECHNICIAN ROUTES */}
      <Route element={<ProtectedRoute allowedRoles={['Agent', 'agent', 'Technician', 'technician', 'Service Desk Agent', 'Admin', 'admin', 'Super Admin', 'superadmin']} requiredPermission="ticket.view" />}>
        <Route element={<ConsoleLayout />}>
          <Route path="/technician" element={<Navigate to="/technician/dashboard" replace />} />
          <Route path="/technician/dashboard" element={<AgentDashboardWrapper />} />
          <Route path="/technician/queue" element={<TechnicianQueueWrapper />} />
          <Route path="/agent" element={<Navigate to="/technician/dashboard" replace />} />
          <Route path="/agent/dashboard" element={<AgentDashboardWrapper />} />
          <Route path="/agent/queue" element={<TechnicianQueueWrapper />} />
        </Route>
      </Route>

      {/* VIEWER ROUTES */}
      <Route element={<ProtectedRoute allowedRoles={['Viewer', 'Auditor', 'Admin', 'admin', 'Super Admin', 'superadmin']} requiredPermission="ticket.view" />}>
        <Route element={<ConsoleLayout />}>
          <Route path="/viewer/dashboard" element={<ViewerDashboardWrapper />} />
          <Route path="/viewer/reports" element={<AdminReportsWrapper />} />
        </Route>
      </Route>

      {/* USER DASHBOARD & REQUESTER PORTAL ROUTES */}
      <Route element={<ProtectedRoute />}>
        <Route path="/user" element={<NormalUserPortalGuard />} />
        <Route path="/user/*" element={<NormalUserPortalGuard />} />
        <Route path="/my-tickets" element={<Navigate to="/user/tickets" replace />} />
      </Route>

      {/* SOC USE CASE PORTAL COMPATIBILITY ROUTES */}
      <Route element={<ProtectedRoute allowedRoles={['Super Admin', 'superadmin', 'Admin', 'admin', 'SOC Admin', 'soc admin', 'SOC Lead', 'SOC Manager', 'SOC Analyst', 'SOC Engineer', 'soc engineer', 'soc']} />}>
        <Route path="/soc-portal" element={<UserDashboardLayout />}>
          <Route index element={<Navigate to="/soc-portal/dashboard" replace />} />
          <Route path="dashboard" element={<UserDashboardHome />} />
          <Route path="use-cases" element={<UserUseCasesList />} />
          <Route path="use-cases/create" element={<UserCreateUseCase />} />
          <Route path="use-cases/drafts" element={<UserDraftsList />} />
          <Route path="use-cases/completed" element={<UserCompletedUseCases />} />
          <Route path="pending-actions" element={<UserPendingActions />} />
          <Route path="in-progress" element={<UserInProgressView />} />
          <Route path="workflow-history" element={<UserWorkflowHistory />} />
          <Route path="notifications" element={<UserNotificationsView />} />
          <Route path="email-history" element={<UserEmailHistoryView />} />
          <Route path="knowledge-base" element={<UserKnowledgeBase />} />
          <Route path="guidelines" element={<UserGuidelinesView />} />
          <Route path="profile" element={<UserProfileView />} />
          <Route path="change-password" element={<UserChangePasswordView />} />
        </Route>
      </Route>
      
      {/* PENDING APPROVAL ROUTE */}
      <Route path="/access-pending" element={
        <div className="flex min-h-screen flex-col items-center justify-center bg-theme p-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-500 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h1 className="text-2xl font-bold text-theme mb-2">Access Pending Approval</h1>
          <p className="text-muted-foreground max-w-md">
            Your Microsoft account has been authenticated, but you do not currently have access to Compass Ticketing. Please contact your administrator.
          </p>
          <button 
            onClick={() => window.dispatchEvent(new Event('compass_auth_changed'))}
            className="mt-8 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
          >
            Refresh Status
          </button>
        </div>
      } />
      
      {/* FORCE PASSWORD CHANGE ROUTE */}
      <Route element={<ProtectedRoute />}>
        <Route path="/force-password-change" element={<ForcePasswordChangeScreen />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function NormalUserPortalGuard() {
  const { user } = useAuth();
  const location = useLocation();
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();

  const isRequester = 
    role === 'user' || 
    role === 'normal user' || 
    role === 'requester' ||
    (!role && !email.includes('admin') && !email.includes('tech') && !email.includes('agent') && !email.includes('pinochle'));

  // If a staff user (Technician, Admin, Manager, SOC, etc.) lands directly on /user or /user/dashboard, route to their dedicated staff dashboard
  if (!isRequester && (location.pathname === '/user' || location.pathname === '/user/' || location.pathname === '/user/dashboard')) {
    const target = user?.effective_redirect || getDashboardRedirectForRole(user?.role, user?.email);
    return <Navigate to={target} replace />;
  }

  return <NormalUserPortal />;
}

function RoleRedirector() {
  const { user } = useAuth();
  
  if (user?.mustChangePassword) return <Navigate to="/force-password-change" replace />;
  
  if (user?.status === 'PENDING' || user?.status === 'INVITED') {
    return <Navigate to="/access-pending" replace />;
  }

  const role = user?.role;
  const email = user?.email;
  const target = user?.effective_redirect || getDashboardRedirectForRole(role, email);

  return <Navigate to={target} replace />;
}
